import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Row, Col, Typography, Statistic, Button, message, Spin, Tag,
  Alert, Space, Descriptions, Divider,
} from 'antd';
import {
  TeamOutlined, EnvironmentOutlined, StarOutlined, MailOutlined,
  EyeOutlined, RocketOutlined, TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import CommunityFeed from '../../components/community/CommunityFeed';
import CommunityHighlights from '../../components/community/CommunityHighlights';
import TeamNewsPanel from '../../components/community/TeamNewsPanel';
import TeamMatchesPanel from '../../components/community/TeamMatchesPanel';
import TeamSlugCard from '../../components/community/TeamSlugCard';

const { Title, Text } = Typography;

export default function TeamOwnerDashboard() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/community/team-owner/${id}/dashboard`);
      setData(r.data);
    } catch (err) {
      message.error(err.response?.data?.message || 'Erro ao carregar');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!data) return null;
  const t = data.team;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 16 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <TeamOutlined /> {t.name}
            {t.verified && <Tag color="blue" style={{ marginLeft: 8 }}>Verificado</Tag>}
          </Title>
          <Text type="secondary">Painel do time na comunidade Futezap</Text>
        </div>
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => nav('/time/mapa')}>Ver no mapa</Button>
          <Button type="primary" onClick={() => nav(`/time/editar/${id}`)}>Editar</Button>
        </Space>
      </div>

      {(!t.city || !t.cep) && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message="Faltam dados do time"
          description="Cidade e CEP não estão preenchidos — sem eles o time não aparece no mapa."
          action={
            <Button size="small" type="primary" onClick={() => nav(`/time/editar/${id}`)}>
              Editar agora
            </Button>
          }
        />
      )}

      {/* CTA: virar cliente Futezap — Card horizontal em vez de Alert
          pra melhor aproveitamento do espaco (botao vertical-centered). */}
      <div style={{
        marginBottom: 16, padding: '14px 18px',
        background: 'linear-gradient(135deg, #e6f7ff 0%, #f0fdf4 100%)',
        border: '1px solid #91d5ff', borderRadius: 12,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <RocketOutlined style={{ fontSize: 26, color: '#128C7E', flexShrink: 0 }} />
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#003a8c', marginBottom: 2 }}>
            Automatiza as convocações no WhatsApp
          </div>
          <div style={{ fontSize: 12, color: '#5a6a63', lineHeight: 1.45 }}>
            Envio automático, ranking, MVP e avaliações. Trial grátis por 30 dias.
          </div>
        </div>
        <Button type="primary" icon={<RocketOutlined />}
          onClick={() => nav(`/time/upgrade/${id}`)}
          style={{ background: '#128C7E', borderColor: '#128C7E', flexShrink: 0 }}>
          Conhecer Futezap Pro
        </Button>
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Convites recebidos" value={data.invites_recebidos.total}
              prefix={<MailOutlined />} valueStyle={{ color: '#25D366' }} />
            <Text type="secondary" style={{ fontSize: 11 }}>{data.invites_recebidos.pendentes} pendentes</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Convites enviados" value={data.invites_enviados.total}
              prefix={<MailOutlined />} />
            <Text type="secondary" style={{ fontSize: 11 }}>{data.invites_enviados.aceitos} aceitos · {data.invites_enviados.pendentes} pendentes</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Avaliação" value={Number(data.ratings.media || 0).toFixed(1)}
              prefix={<StarOutlined />} valueStyle={{ color: '#faad14' }} />
            <Text type="secondary" style={{ fontSize: 11 }}>{data.ratings.total} avaliações</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Jogos realizados" value={data.historico.total_jogos}
              prefix={<TrophyOutlined />} />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Dados do time" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
          <Descriptions.Item label="Cidade">{t.city ? `${t.city}${t.state ? '/' + t.state : ''}` : '-'}</Descriptions.Item>
          <Descriptions.Item label="Bairro">{t.neighborhood || '-'}</Descriptions.Item>
          <Descriptions.Item label="Quadra">{t.court_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="CEP">{t.cep || '-'}</Descriptions.Item>
          <Descriptions.Item label="Nível">{t.skill_level}</Descriptions.Item>
          <Descriptions.Item label="Público no mapa">
            {t.is_public ? <Tag color="green">Sim</Tag> : <Tag>Escondido</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Valor avulso">{t.guest_price ? `R$ ${Number(t.guest_price).toFixed(2)}` : '-'}</Descriptions.Item>
          <Descriptions.Item label="Mensalidade">{t.monthly_price ? `R$ ${Number(t.monthly_price).toFixed(2)}` : '-'}</Descriptions.Item>
          <Descriptions.Item label="Raio de busca">{t.search_radius_km || 15} km</Descriptions.Item>
          <Descriptions.Item label="Cadastrado em">{t.created_at ? new Date(t.created_at).toLocaleDateString('pt-BR') : '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <TeamSlugCard
        currentSlug={t.slug}
        updateEndpoint={`/community/team-owner/${id}`}
        onSaved={load}
      />

      <TeamNewsPanel teamId={id} isOwner={true} />

      <TeamMatchesPanel teamId={id} teamName={t.name} />

      <CommunityHighlights />

      <Card size="small" style={{ marginBottom: 16 }}>
        <CommunityFeed compact />
      </Card>

      <Card size="small" title="Convites & mensagens">
        <Text type="secondary">Gerencie convites, aceite candidatos e converse com jogadores.</Text>
        <Divider />
        <Space>
          <Button icon={<MailOutlined />} type="primary" onClick={() => nav('/time/convites')}>Ver todos os convites</Button>
          <Button icon={<EnvironmentOutlined />} onClick={() => nav('/time/procurar')}>Buscar jogadores</Button>
        </Space>
      </Card>
    </div>
  );
}
